import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [{ label: t('aboutUs') }];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '反差设计研究所',
    url: 'https://boldniche.github.io',
    description: '专注探索反差美学在视觉设计、摄影、文案与品牌营销中的无限可能',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://boldniche.github.io/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 反差设计研究所',
    description: '反差设计研究所——专注探索反差美学在视觉设计、摄影、文案与品牌营销中的无限可能',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u3l0v8l97n3eas3gng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.includes('spam')) {
          setFormStatus('success');
        } else {
          setFormStatus('error');
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Page Header */}
        <section className="relative w-full h-[260px] md:h-[320px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20studio%20space%20with%20warm%20lighting%20clean%20white%20walls%20wooden%20desk%20creative%20workspace%20artistic%20atmosphere%20soft%20shadows%20modern%20design%20office%20with%20large%20windows%20natural%20light&width=1800&height=600&seq=about-header&orientation=landscape"
            alt="关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">
              {t('aboutUs')}
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-3">探索反差，重塑表达</p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-5">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              品牌故事
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                反差设计研究所成立于2024年，由一群深耕设计、摄影、文案与品牌营销领域的创作者共同发起。我们相信，「反差」是创作中最被低估的力量——它不是简单的对立，而是一种在矛盾中发现统一、在冲突中寻找平衡的智慧。
              </p>
              <p>
                在这个信息过载的时代，平庸的表达注定被淹没。我们致力于帮助每一位创作者——无论你是设计师、摄影师、文案写手还是品牌运营者——掌握反差美学的底层逻辑，让你的作品拥有直击人心的力量。
              </p>
              <p>
                我们的内容覆盖六大核心领域：视觉反差设计、强反差摄影构图、反差色彩搭配、反差文案技巧、反差营销策略和反差感人设。从理论到实战，从案例分析到创作工具，我们提供系统化的反差美学知识体系。
              </p>
            </div>
          </div>
        </section>

        {/* Stats / Reputation */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              口碑与影响力
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: '120+', label: '深度原创文章' },
                { value: '50万+', label: '月均阅读量' },
                { value: '3万+', label: '社群成员' },
                { value: '98%', label: '读者好评率' },
              ].map((stat, index) => (
                <div key={index} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{stat.value}</p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              读者评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  quote: '作为一名设计师，我一直在寻找能提升作品"冲击力"的方法。反差设计研究所的内容让我重新理解了色彩和构图——原来"对比"可以这么系统地学习和应用。',
                  name: '王思远',
                  role: '视觉设计师',
                },
                {
                  quote: '文案写作中最难的就是"让人停下来看"。学习了反差文案技巧后，我的公众号标题打开率提升了3倍。这不仅是技巧，更是一种全新的思维方式。',
                  name: '李新月',
                  role: '内容运营经理',
                },
                {
                  quote: '反差营销策略那篇文章彻底改变了我对品牌定位的看法。我们公司按照反差的思路重新梳理了品牌传播方案，效果超出预期。',
                  name: '张铭轩',
                  role: '品牌总监',
                },
              ].map((testimonial, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 flex flex-col"
                >
                  <i className="ri-double-quotes-l text-3xl text-primary-300 mb-3"></i>
                  <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                    {testimonial.quote}
                  </p>
                  <div className="mt-4 pt-4 border-t border-background-200/70">
                    <p className="text-sm font-medium text-foreground-900">{testimonial.name}</p>
                    <p className="text-xs text-foreground-500">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3 text-center">
              {t('contactUs')}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或合作意向，欢迎与我们取得联系
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="contact-form-website">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                  placeholder="想聊什么？"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                  placeholder="请详细描述你的问题或合作需求（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <button
                type="submit"
                className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-secondary-700 bg-secondary-100 px-4 py-3 rounded-lg text-center">
                  留言已发送！我们会尽快与你取得联系。
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-accent-700 bg-accent-100 px-4 py-3 rounded-lg text-center">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}